import { ProLayout } from '@ant-design/pro-components';
import { Button, Select, Watermark } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AvatarDropdown, AvatarName, Footer, Question } from '@/common/components';
import { useAppContext } from '@/common/hooks/AppStateProvider';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { buildMenuData } from '@/common/utils/route';
import { authClient } from '@/common/utils/auth-client';

const APP_TITLE = '餐饮连锁经营智能体';
const APP_HOME_PATH = '/';
const MANAGER_ROLES = new Set(['owner', 'admin']);

const filterMenuRoutesByRole = (routes, role) => {
  if (MANAGER_ROLES.has(role)) {
    return routes;
  }
  return routes.filter((route) => route.path !== 'settings');
};

const resolveOrganizationMenuData = (items, resolvePath) => {
  return items.map((item) => ({
    ...item,
    path: resolvePath(item.path),
    routes: item.routes ? resolveOrganizationMenuData(item.routes, resolvePath) : undefined,
  }));
};

export const resolveSwitchedOrganizationPath = ({
  currentOrganizationId,
  hash = '',
  nextOrganizationId,
  pathname,
  search = '',
}) => {
  const nextOrganizationRoot = `/${encodeURIComponent(nextOrganizationId)}`;
  const currentOrganizationRoot = `/${encodeURIComponent(currentOrganizationId)}`;
  const nextPathname =
    pathname === currentOrganizationRoot || pathname.startsWith(`${currentOrganizationRoot}/`)
      ? `${nextOrganizationRoot}${pathname.slice(currentOrganizationRoot.length)}`
      : nextOrganizationRoot;
  return `${nextPathname}${search}${hash}`;
};

const OrganizationSwitcher = () => {
  const location = useLocation();
  const { organizationId } = useOrganizationPath();
  const [organizations, setOrganizations] = useState([]);

  useEffect(() => {
    let active = true;
    authClient.organization
      .list()
      .then(({ data, error }) => {
        if (!active || error) {
          return;
        }
        setOrganizations(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  if (!organizationId || organizations.length === 0) {
    return null;
  }

  return (
    <Select
      aria-label="切换组织"
      onChange={(nextOrganizationId) => {
        window.location.assign(
          resolveSwitchedOrganizationPath({
            currentOrganizationId: organizationId,
            hash: location.hash,
            nextOrganizationId,
            pathname: location.pathname,
            search: location.search,
          }),
        );
      }}
      options={organizations.map((organization) => ({
        label: organization.name || organization.id,
        value: organization.id,
      }))}
      popupMatchSelectWidth={false}
      size="small"
      value={organizationId}
      variant="borderless"
    />
  );
};

const AppLayout = ({ routes }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useAppContext();
  const { resolveOrganizationPath } = useOrganizationPath();
  const menuData = useMemo(() => {
    return resolveOrganizationMenuData(
      buildMenuData(filterMenuRoutesByRole(routes, session.role)),
      resolveOrganizationPath,
    );
  }, [resolveOrganizationPath, routes, session.role]);
  return (
    <ProLayout
      title={APP_TITLE}
      logo="/favicon.png"
      layout="mix"
      contentWidth="Fluid"
      fixSiderbar
      location={location}
      route={{ routes: menuData }}
      menuItemRender={(item, dom) => (item.path ? <Link to={item.path}>{dom}</Link> : dom)}
      actionsRender={() => [
        <Question key="doc" />,
        <AvatarDropdown key="user" menu>
          <Button size="small" type="text">
            <AvatarName />
          </Button>
        </AvatarDropdown>,
        <OrganizationSwitcher key="organization" />,
      ]}
      footerRender={false}
      menuFooterRender={() => <Footer />}
      onMenuHeaderClick={() => navigate(resolveOrganizationPath(APP_HOME_PATH))}
    >
      <Watermark
        content={`紫光园${session.user?.name ? `·${session.user.name}` : ''}`}
        gap={[256, 256]}
        offset={[256, 256]}
      >
        <Outlet />
      </Watermark>
    </ProLayout>
  );
};
export default AppLayout;
